/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { SeedMix, SeedProductLine } from '../types/SeedOrderFormType';
import { allSeedsKg, calculateQuantity, calculateSowingRate } from '../utils/calculateTotal';
import { PaddockChangedIndex, ProductChangedIndex } from '../utils/getAllProductIds';

export function ceilToTwoDecimals(value: string | number): number {
  const parsedValue = parseFloat(value as string);

  if (isNaN(parsedValue)) {
    throw new Error('Invalid number');
  }

  // Ceil the value to the nearest two decimals
  const result = Math.ceil(parsedValue * 100) / 100;

  return result;
}

export const updateQuantity = (changedQuantity: ProductChangedIndex[], mixes: SeedMix[]) => {
  const updatedMixes = mixes.map((mix, mixIndex) => {
    const updatedProducts = mix.products.map((product, productIndex) => {
      const changeIndex = changedQuantity.find(
        (change) => change.mixIndex === mixIndex && change.productIndex === productIndex,
      );
      if (changeIndex) {
        const { quantity, roundedUp } = calculateQuantity(mixes, mixIndex, productIndex);
        return {
          ...product,
          quantity,
          quantityRoundedUp: roundedUp,
        };
      }
      return product;
    });

    return {
      ...mix,
      products: updatedProducts,
    };
  });

  return updatedMixes;
};

export const updatesowingRate = (changedQuantity: ProductChangedIndex[], mixes: SeedMix[]) => {
  const updatedMixes = mixes.map((mix, mixIndex) => {
    const updatedProducts = mix.products.map((product, productIndex) => {
      const changeIndex = changedQuantity.find(
        (change) => change.mixIndex === mixIndex && change.productIndex === productIndex,
      );
      if (changeIndex) {
        const newSowingRate = calculateSowingRate(mixes, mixIndex, productIndex);
        return {
          ...product,
          sowingRate: newSowingRate,
          quantityRoundedUp: false,
        };
      }
      return product;
    });

    return {
      ...mix,
      products: updatedProducts,
    };
  });

  return updatedMixes;
};
export const updateQuantityFromPaddock = (
  changedQuantity: PaddockChangedIndex[],
  mixes: SeedMix[],
) => {
  const updatedMixes = mixes.map((mix, mixIndex) => {
    if (changedQuantity.some((change) => change.mixIndex === mixIndex)) {
      const updatedProducts = mix.products.map((product, productIndex) => {
        const { quantity, roundedUp } = calculateQuantity(mixes, mixIndex, productIndex);
        return {
          ...product,
          quantity,
          quantityRoundedUp: roundedUp,
        };
      });

      return {
        ...mix,
        products: updatedProducts,
      };
    }
    return mix;
  });

  const finalMixes = updateMixingFeeQuantity(updatedMixes);
  return finalMixes;
};
const updateMixingFeeQuantity = (mixes: SeedMix[]) => {
  const updatedMixes = mixes.map((mix) => {
    const index = mix.products.findIndex((product: SeedProductLine) => product.isMixingFee);
    if (index >= 0) {
      const quantity = allSeedsKg(mix.products);
      if (quantity > 0) {
        const updatedProducts = mix.products.map((product, productIndex) => {
          if (productIndex === index) {
            return {
              ...product,
              quantity,
              quantityRoundedUp: false,
            };
          }
          return product;
        });

        return {
          ...mix,
          products: updatedProducts,
        };
      }
    }
    return mix;
  });

  return updatedMixes;
};
